import "@/assets/index.css";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import type { LDFlagSet } from "launchdarkly-react-client-sdk";
import ReactDOM from "react-dom/client";
import { BrowserRouter, RouterProvider } from "react-router-dom";

import BlockedByOtherTabs from "@/components/BlockedByOtherTabs";
import StorageError from "@/components/StorageError";
import { config, flags, setApiTelemetryContext } from "@/config.ts";
import { markWorkspacesDelivered, updateWorkspaceConfig } from "@/data/workspaceConfig";
import { incrementBootReloadStreak, reportBootReloadLoop } from "@/init/bootLoop.ts";
import { initializeBootTelemetry } from "@/init/bootTelemetry.ts";
import { initializeFlightRecorder } from "@/init/flightRecorder.ts";
import { initializeGoogleAnalytics } from "@/init/googleAnalytics.ts";
import { initializeHoneycomb } from "@/init/honeycomb.ts";
import { createLDClient, initializeLaunchDarkly } from "@/init/launchDarkly.tsx";
import { initializeLogLevel } from "@/init/loglevel.ts";
import { initializePerf } from "@/init/perf.ts";
import { initializePreloadErrorHandler } from "@/init/preloadError.ts";
import { initializeSentry } from "@/init/sentry.ts";
import { initializeStallDetector } from "@/init/stallDetector.ts";
import { isStandaloneQrLoginPage } from "@/init/standalonePage.ts";
import { initializeTimings } from "@/init/timings.ts";
import { initializeWebVitals } from "@/init/webVitals.ts";
import { initializeLogChannels } from "@/logger";
import { queryClient } from "@/queryClient.ts";
import { router } from "@/routes";
import QrLogin from "@/routes/qrLogin";
import {
  currentBootPhase,
  type ErrorScreenKind,
  initializeTabVisibility,
  recordEventAndFlush,
  SessionAttributesSpanProcessor,
  SessionCategorySampler,
  setSessionAttribute,
} from "@/services/telemetry";
import { initializeLocalStore } from "@/store/boot";
import { user, workspaceId } from "@/store/scope";
import { store } from "@/store/store";
import theme from "@/theme/theme";
import { isElectron } from "@/utils";
import { isQuotaExceededError, localStorageCheck } from "@/utils/localStorage.ts";
import { requestPersistentStorage } from "./init/persistentStorage";
import { runStorageProbe } from "./init/storageProbe";

const isDebug = config.debugMode;
localStorageCheck();

/** A store batch open this long is holding back live updates, not coalescing writes. */
const LONG_OPEN_BATCH_WARNING_MS = 250;

if (isDebug) {
  store.warnOnLongOpenBatches(LONG_OPEN_BATCH_WARNING_MS);
}

/** Upper bound on how long the pre-recovery boot.failed flush may delay the auto-recovery reload. */
const BOOT_FAILED_FLUSH_BUDGET_MS = 1500;

/** Drops the static index.html splash once React is about to paint something real. */
function removeBootSplash(): void {
  document.getElementById("boot-splash")?.remove();
}

/** Best-effort current workspace id from the URL, attached as context to perf rollups. */
function workspaceIdFromPathname(): string | undefined {
  return window.location.pathname.match(/\/workspaces\/(?<id>[^/]+)/)?.groups?.id;
}

/**
 * Boots the OTel/Honeycomb exporter with the per-session category sampler, the mutable
 * session-attribute stamper, and static build/surface resource attributes. `active: false` (the
 * `inActiveTelemetry` kill switch) never starts the SDK, so no spans leave the page.
 */
/** Guards against a second `initializeHoneycomb` (which would create a duplicate SDK). */
let telemetryStarted = false;

function startTelemetry(active: boolean): void {
  telemetryStarted = true;
  initializeHoneycomb({
    ingestApiKey: import.meta.env.VITE_HONEYCOMB_INGEST_API_KEY,
    serviceName: `web-${import.meta.env.VITE_HONEYCOMB_ENVIRONMENT}`,
    active,
    sampler: new SessionCategorySampler({
      getSampleRates: () => flags.telemetrySampleRates,
      isKilled: () => flags.inActiveTelemetry === true,
    }),
    spanProcessors: [new SessionAttributesSpanProcessor()],
    resourceAttributes: {
      "app.build_version": config.buildVersion ?? "",
      "app.build_sha": config.buildSha ?? "",
      // Always-populated release id (release/rc tag, else build sha) so per-deploy regression
      // breakdowns have something to group on; `app.build_sha` gives exact-commit attribution.
      "app.release": config.release ?? "",
      "app.surface": isElectron ? "electron" : "web",
    },
  });
}

window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason;
  if (isQuotaExceededError(error)) {
    event.preventDefault();
    console.warn("[STORAGE-QUOTA] Caught unhandled QuotaExceededError in promise rejection, running cleanup");
    localStorageCheck();
  }
});

const BOOT_RECOVERY_MARKER_KEY = "storyboard.bootRecoveryAttempt.v2";
const BOOT_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

/**
 * Try to fix an unrecoverable boot failure automatically with one bounded reload; once a reload
 * has been tried within the recovery window, fall through to the StorageError screen instead of
 * reload-looping. Store restore failures degrade to a cold bootstrap on their own inside
 * `initializeLocalStore`, so anything landing here is an init failure (LaunchDarkly, router).
 */
async function renderRecoveryOrError(reason: string, { cause }: { cause?: unknown } = {}): Promise<void> {
  await Promise.race([
    recordEventAndFlush("boot.failed", { boot: { reason } }),
    new Promise<void>((resolve) => setTimeout(resolve, BOOT_FAILED_FLUSH_BUDGET_MS)),
  ]);
  let recentAttempt = false;
  try {
    const marker = localStorage.getItem(BOOT_RECOVERY_MARKER_KEY);
    recentAttempt = marker != null && Date.now() - Number(marker) < BOOT_RECOVERY_WINDOW_MS;
    if (!recentAttempt) localStorage.setItem(BOOT_RECOVERY_MARKER_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable: skip loop protection rather than the recovery itself
  }
  if (!recentAttempt) {
    window.location.reload();
    return;
  }
  const kind: ErrorScreenKind = "storage";
  Sentry.captureException(cause instanceof Error ? cause : new Error(reason), {
    tags: { phase: "app_boot", errorScreen: kind },
    extra: { reason },
  });
  await recordEventAndFlush("error_screen.rendered", { error: { kind, reason } });
  removeBootSplash();
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <StorageError reason={reason} />
    </ThemeProvider>,
  );
}

/** Thrown to unwind the boot after a screen has already been rendered, so the catch does not re-handle it. */
class BootHandled extends Error {}

/**
 * Renders the close-other-tabs state and stops the boot. Continuing would leave the app reading
 * names that queue behind a delete another tab is holding open, which is the dead spinner this
 * screen exists to replace.
 */
async function renderBlockedByOtherTabs(): Promise<void> {
  await recordEventAndFlush("error_screen.rendered", { error: { kind: "storage", reason: "blocked_by_other_tabs" } });
  removeBootSplash();
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BlockedByOtherTabs />
    </ThemeProvider>,
  );
}

/** Boots the full application: trackers, LaunchDarkly, Sentry, the local store, and the router. */
async function bootApp(): Promise<void> {
  // Stamp account and workspace from the auth session / URL up front so *every* span — including
  // events buffered before the exporter starts — is attributable. Set before telemetry starts
  // (session attributes are just a map read at span start) so the buffered-event replay inside
  // startTelemetry() carries them. Bootstrap and MyAccountProvider later refresh them from the
  // local record once it resolves.
  setSessionAttribute("account.id", user?.accounts?.[0]?.id);
  setSessionAttribute("workspace.id", workspaceId ?? undefined);

  // Seed the workspace configs from the userinfo this boot already fetched, before the first render. A warm boot
  // renders from the persisted snapshot at once, while `UserProvider` records the configs only in an effect after it:
  // unseeded, the first render reads every workspace setting as absent, and `FirstLocationRedirect` routes a Dispatch
  // workspace to the driver view before the config arrives (WEB-2349). That userinfo is the API's delivery of the
  // workspaces, so the Dispatch gate reads on or off from the first render rather than "loading".
  for (const workspace of user?.workspaces ?? []) updateWorkspaceConfig(workspace);
  if (user?.workspaces) markWorkspacesDelivered();

  // Count this page load toward the reload-loop detector before anything can fail — but only when a
  // boot is actually plausible (signed in, on a workspace route). A signed-out user reloading the
  // login route, or a repeat visitor to non-workspace routes, must not accrue a loop. A boot that
  // reaches interactive resets the streak (see bootTelemetry); emitting the event waits for telemetry.
  const bootPlausible = Boolean(user?.accounts?.[0]) && Boolean(workspaceId);
  const bootReloadStreak = bootPlausible ? incrementBootReloadStreak() : 0;

  const ldClient = createLDClient(import.meta.env.VITE_LD_CLIENT_SIDE_ID);
  const activeLdClient = initializeLaunchDarkly(ldClient, import.meta.env.VITE_LD_CLIENT_SIDE_ID);
  initializeLogLevel({ logLevel: import.meta.env.VITE_LOG_LEVEL });
  initializeLogChannels();

  initializeSentry({
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    dsn: "https://f8871862445f42c0934637c048b69182@o1146727.ingest.sentry.io/4505133231702016",
    mode: import.meta.env.MODE,
    release: config.release,
  });

  initializeGoogleAnalytics({ key: import.meta.env.VITE_GA_KEY });

  initializePreloadErrorHandler();

  initializeTimings();

  // Measure IndexedDB write latency and attach it as Sentry *context* (storage_io) so any later
  // unrecoverable capture carries the storage I/O picture; the probe itself never emits an event.
  runStorageProbe().then(() => {});

  // Ask the browser to exempt this origin's storage (the snapshot + registry stores) from eviction
  requestPersistentStorage().then(() => {});

  try {
    const [LDProvider, storeBoot] = await Promise.all([activeLdClient, initializeLocalStore()]);
    if (storeBoot.blockedByOtherTabs) {
      // Start the exporter before rendering: this branch never reaches the normal `startTelemetry`
      // below, and the blocked-rate events are the only measure of how often the new screen fires
      // during an era changeover. Buffered events are dropped with the page if telemetry never starts.
      if (!telemetryStarted) {
        startTelemetry(!(ldClient?.allFlags()?.inActiveTelemetry ?? false));
      }
      await renderBlockedByOtherTabs();
      throw new BootHandled();
    }

    const allFlags: LDFlagSet = ldClient?.allFlags();
    const inActiveTelemetry = allFlags?.inActiveTelemetry ?? false;
    startTelemetry(!inActiveTelemetry);
    reportBootReloadLoop(bootReloadStreak);

    // API-layer spans need boot context (cold/warm/incremental) the request layer can't know.
    setApiTelemetryContext(() => ({ "app.boot_phase": currentBootPhase() ?? "unknown" }));
    initializeTabVisibility();
    initializeBootTelemetry();
    if (!inActiveTelemetry) {
      initializeWebVitals();
      initializeStallDetector();
    }

    initializeFlightRecorder();
    initializePerf({
      active: !inActiveTelemetry,
      apiHosts: [config.baseDomain, config.graphqlUrl].filter(Boolean),
      getContext: () => ({ workspaceId: workspaceIdFromPathname() }),
    });

    const routerWrapper = LDProvider ? (
      <LDProvider>
        <RouterProvider router={router} />
      </LDProvider>
    ) : (
      <>
        <RouterProvider router={router} />
      </>
    );

    removeBootSplash();
    ReactDOM.createRoot(document.getElementById("root")!).render(
      <QueryClientProvider client={queryClient}>{routerWrapper}</QueryClientProvider>,
    );

    try {
      localStorage.removeItem(BOOT_RECOVERY_MARKER_KEY);
    } catch {
      // ignore
    }
  } catch (e) {
    if (!(e instanceof BootHandled)) {
      // Any fatal boot failure (LaunchDarkly init, router render, store boot throwing unexpectedly)
      // gets one bounded auto-recovery reload, then the StorageError screen. LaunchDarkly can reject
      // before startTelemetry ran, so start the exporter here — otherwise the flushed boot.failed /
      // error_screen events never ship. A BootHandled unwind is excluded: its screen is already up,
      // and recovering would reload over it.
      if (!telemetryStarted) {
        startTelemetry(!(ldClient?.allFlags()?.inActiveTelemetry ?? false));
        reportBootReloadLoop(bootReloadStreak);
      }
      await renderRecoveryOrError(e instanceof Error ? e.message : String(e), { cause: e });
    }
  }
}

// /qr-login is a static, unauthenticated store-fallback page whose URL carries a one-time sign-in
// token (SVS-3635). It renders before any tracker or boot infrastructure initializes — Sentry, GTM,
// LaunchDarkly, the local store — so the token cannot reach analytics or error reporting, and a
// phone with broken storage cannot land on a boot error screen instead of the store badges.
// store/scope.ts consults the same predicate at import time to skip its userinfo fetch, which runs
// before any statement here and whose 401 handler would redirect an anonymous visitor to auth.
if (isStandaloneQrLoginPage()) {
  removeBootSplash();
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <QrLogin />
      </BrowserRouter>
    </ThemeProvider>,
  );
} else {
  await bootApp();
}
